import type { StopsFilterChange } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';

export type EvoStopsOption = 'ANY' | 'DIRECT' | 'UP_TO_ONE' | 'UP_TO_TWO';

export type EvoStopsState = {
  outboundStops?: StopsFilter[];
  inboundStops?: StopsFilter[];
  directOnly?: DirectOnlyFilter;
};

const boundHasEnabledStop = (bound: StopsFilter[] | undefined, value: StopsFilterValue): boolean =>
  !!bound?.some((stopsFilter) => stopsFilter.stops === value && stopsFilter.enabled);

const hasEnabledStopsValue = (bounds: (StopsFilter[] | undefined)[], value: StopsFilterValue): boolean =>
  bounds.some((bound) => boundHasEnabledStop(bound, value));

export function getEvoStopsOptions(state: EvoStopsState): EvoStopsOption[] {
  const { outboundStops, inboundStops, directOnly } = state;
  const bounds = [outboundStops, inboundStops];
  const options: EvoStopsOption[] = ['ANY'];
  // Without the trip-level direct-only facet, DIRECT maps to per-leg NO_STOPS selections.
  // The query builder only emits enabled+selected items, so both bounds must expose an
  // enabled direct option or one leg would silently stay unfiltered (MPH-653).
  const bothBoundsHaveDirect =
    boundHasEnabledStop(outboundStops, 'NO_STOPS') && boundHasEnabledStop(inboundStops, 'NO_STOPS');
  // A residual state may still carry a direct selection (asymmetric refine echo);
  // keep DIRECT visible then, so the checked radio always exists on screen.
  if (directOnly || bothBoundsHaveDirect || deriveEvoStopsOption(state) === 'DIRECT') options.push('DIRECT');
  // Enabled-aware like DIRECT above: an option whose top value is disabled everywhere selects the
  // exact same stops as the option below it, so it would render as a radio that can never be
  // checked — the click would land on its neighbour instead.
  // One enabled bound is enough here, unlike DIRECT's both-bounds rule: a leg with no flight under
  // the cap cannot be narrowed by any query, so the option still filters the other leg. Hiding it
  // would leave the trip with radios that do nothing instead of one that restricts what it can.
  if (hasEnabledStopsValue(bounds, 'ONE')) options.push('UP_TO_ONE');
  if (hasEnabledStopsValue(bounds, 'TWO_OR_MORE')) options.push('UP_TO_TWO');
  return options;
}

/** What the query builder can actually send for a bound: enabled items only (see transportsFetch). */
const getSelectedValues = (bound: StopsFilter[]): StopsFilterValue[] =>
  bound.filter((stopsFilter) => stopsFilter.enabled && stopsFilter.selected).map((stopsFilter) => stopsFilter.stops);

/** The option's values this bound can actually hold — a leg only offers the stop counts it flies. */
const getAvailableValues = (bound: StopsFilter[], values: StopsFilterValue[]): StopsFilterValue[] =>
  bound
    .filter((stopsFilter) => stopsFilter.enabled && values.includes(stopsFilter.stops))
    .map((stopsFilter) => stopsFilter.stops);

const isSameValueSet = (values: StopsFilterValue[], expected: StopsFilterValue[]): boolean =>
  values.length === expected.length && expected.every((value) => values.includes(value));

const OPTION_VALUES: { [key in Exclude<EvoStopsOption, 'ANY'>]: StopsFilterValue[] } = {
  DIRECT: ['NO_STOPS'],
  UP_TO_ONE: ['NO_STOPS', 'ONE'],
  UP_TO_TWO: ['NO_STOPS', 'ONE', 'TWO_OR_MORE'],
};

export function deriveEvoStopsOption(state: EvoStopsState): EvoStopsOption {
  const { outboundStops, inboundStops, directOnly } = state;
  if (directOnly?.selected) return 'DIRECT';

  const boundsWithSelections = [outboundStops, inboundStops].filter(
    (bound): bound is StopsFilter[] => !!bound && getSelectedValues(bound).length > 0
  );
  if (boundsWithSelections.length === 0) return 'ANY';

  // A bound only carries the stop counts its own leg flies, so an option is on when every bound
  // holds the values it actually offers for it — not the full nominal set. A route with no direct
  // flights has no NO_STOPS item at all, and comparing against a fixed ['NO_STOPS', 'ONE'] left
  // UP_TO_ONE underivable: the radio bounced straight back to ANY on every click (MPH-974).
  const isSelectedOption = (option: Exclude<EvoStopsOption, 'ANY'>): boolean =>
    boundsWithSelections.every((bound) =>
      isSameValueSet(getSelectedValues(bound), getAvailableValues(bound, OPTION_VALUES[option]))
    );

  if (isSelectedOption('DIRECT')) return 'DIRECT';
  if (isSelectedOption('UP_TO_ONE')) return 'UP_TO_ONE';
  if (isSelectedOption('UP_TO_TWO')) return 'UP_TO_TWO';
  return 'ANY';
}

// Disabled items are dropped by the query builder, so selecting one would desync the local state
// from the refine response and make the option underivable on the way back.
const selectValuesInBound = (bound: StopsFilter[], values: StopsFilterValue[]): StopsFilter[] =>
  bound.map((stopsFilter) => ({
    ...stopsFilter,
    selected: stopsFilter.enabled && values.includes(stopsFilter.stops),
  }));

export function buildEvoStopsNewState(option: EvoStopsOption, state: EvoStopsState): StopsFilterChange['newState'] {
  const { outboundStops, inboundStops, directOnly } = state;

  const valuesByOption: { [key in EvoStopsOption]: StopsFilterValue[] } = {
    ANY: [],
    DIRECT: directOnly ? [] : OPTION_VALUES.DIRECT,
    UP_TO_ONE: OPTION_VALUES.UP_TO_ONE,
    UP_TO_TWO: OPTION_VALUES.UP_TO_TWO,
  };
  const values = valuesByOption[option];

  return {
    ...(outboundStops && { outboundStops: selectValuesInBound(outboundStops, values) }),
    ...(inboundStops && { inboundStops: selectValuesInBound(inboundStops, values) }),
    ...(directOnly && { directOnly: { ...directOnly, selected: option === 'DIRECT' } }),
  };
}
